import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { MailboxConnectCard } from '@/components/integrations/MailboxConnectCard';
import type { ConnectedAccount } from '@/hooks/integrations/useConnectedAccounts';
import { useConnectedAccounts } from '@/hooks/integrations/useConnectedAccounts';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { HintText } from '@/lib/base/forms/hint-text';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { RadioCard, RadioGroup } from '@/lib/base/radio-buttons';
import { getMicrosoftClientId } from '@/lib/config';
import { SkipButton } from '@/lib/self-service/components/SkipButton';

import { useJoinOnboardingStatus } from '../../api';
import {
  dashboardPath,
  findJoinStepIndex,
  JOIN_STEPS,
  joinStepPath,
} from '../../constants';
import { useJoinFlowState } from '../../flowState';
import { JoinExclusionEditor } from './JoinExclusionEditor';

const FULL_INBOX_TITLE = 'Full inbox';
const FULL_INBOX_BODY =
  'Ahoy reads your email to prep the best follow-ups. Most people start here.';
const EXCLUDE_TITLE = 'Exclude some labels or domains';
const EXCLUDE_BODY =
  'Full access, minus anything you name. Keep specific threads out of Ahoy.';
const RECOMMENDED_BADGE_PROPS = {
  type: 'pill-color',
  color: 'brand',
  size: 'sm',
} as const;

// Mirrors OutlookIntegration.tsx: without a client id the Microsoft OAuth trip
// cannot complete, so offering the button would be offering a dead end.
const IS_MICROSOFT_CONFIGURED = Boolean(getMicrosoftClientId());

/**
 * One state machine for the surface instead of a ladder of booleans
 * (REACT_19_PATTERNS § "one surface state machine").
 */
type PermissionsView =
  | { kind: 'loading' }
  | { kind: 'error' }
  | {
      kind: 'connect';
      accounts: ConnectedAccount[];
      hasUnmatchedAccount: boolean;
    }
  | { kind: 'ready' };

export function PermissionsStep() {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { state, updateState } = useJoinFlowState();
  const { data: status, isLoading, isError } = useJoinOnboardingStatus(orgId);
  const { accounts, loading: accountsLoading } = useConnectedAccounts({
    // The route param is the INVITED org, which is not necessarily the org
    // context auto-selected elsewhere. It is also what `connect-for-team`
    // receives, and therefore what pins a newly connected mailbox to the right
    // workspace.
    orgId: orgId === '' ? undefined : orgId,
  });

  const [attempted, setAttempted] = useState(false);

  const nextPath = useMemo(() => {
    const idx = findJoinStepIndex('permissions');
    const next = idx >= 0 ? JOIN_STEPS[idx + 1] : undefined;
    return next ? joinStepPath(orgId, next.id) : dashboardPath(orgId);
  }, [orgId]);

  const goNext = useCallback(() => navigate(nextPath), [navigate, nextPath]);

  const view = useMemo<PermissionsView>(() => {
    if (isLoading || accountsLoading) {
      return { kind: 'loading' };
    }
    if (isError || status === undefined) {
      return { kind: 'error' };
    }
    if (status.has_mailbox_account) {
      return { kind: 'ready' };
    }
    // `has_mailbox_account` is the server's answer to "will completion actually
    // provision a mailbox", and it is STRICTER than "an account is connected":
    // `find_matching_oauth_accounts` only matches a gmail/outlook_mail account
    // whose address equals the user's Ahoy login address. So a connected
    // account with `has_mailbox_account: false` means the invitee connected a
    // DIFFERENT address, and any exclusions they set here would be silently
    // dropped at completion. Say so rather than letting it fail quietly.
    return {
      kind: 'connect',
      accounts,
      hasUnmatchedAccount: accounts.length > 0,
    };
  }, [isLoading, accountsLoading, isError, status, accounts]);

  const handleVisibility = useCallback(
    (v: string) => {
      if (v === 'full') {
        updateState({
          mailboxVisibility: 'full',
          filteredEmails: [],
          filteredDomains: [],
        });
      } else {
        updateState({ mailboxVisibility: 'exclude' });
      }
    },
    [updateState]
  );

  const handleEmailsChange = useCallback(
    (emails: string[]) => updateState({ filteredEmails: emails }),
    [updateState]
  );

  const handleDomainsChange = useCallback(
    (domains: string[]) => updateState({ filteredDomains: domains }),
    [updateState]
  );

  const excludeEmpty =
    state.mailboxVisibility === 'exclude' &&
    state.filteredEmails.length === 0 &&
    state.filteredDomains.length === 0;
  const blocked = state.mailboxVisibility === null || excludeEmpty;

  const handleContinue = useCallback(() => {
    setAttempted(true);
    if (blocked) {
      return;
    }
    goNext();
  }, [blocked, goNext]);

  let inner: ReactNode;

  switch (view.kind) {
    case 'loading':
      inner = <LoadingIndicator />;
      break;

    case 'error':
      inner = (
        <div className='space-y-4'>
          <p
            role='alert'
            className='text-sm text-[var(--color-text-secondary)]'
          >
            We couldn&rsquo;t load your settings. You can continue and set this
            later.
          </p>
          <Button size='lg' className='w-full' onClick={goNext}>
            Continue
          </Button>
        </div>
      );
      break;

    case 'connect':
      inner = (
        <div className='space-y-6'>
          <div className='space-y-2'>
            <h2 className='text-lg font-semibold text-[var(--color-text-primary)]'>
              Connect your inbox
            </h2>
            <p className='text-sm text-[var(--color-text-secondary)]'>
              Ahoy reads your email to prep follow-ups and keep your contacts up
              to date. You can choose what it sees once it&rsquo;s connected.
            </p>
          </div>

          <div className='space-y-3'>
            <MailboxConnectCard
              orgId={orgId}
              provider='google'
              isConnected={false}
            />
            {IS_MICROSOFT_CONFIGURED && (
              <MailboxConnectCard
                orgId={orgId}
                provider='microsoft'
                isConnected={false}
              />
            )}
          </div>

          {view.hasUnmatchedAccount && (
            <div role='alert'>
              <HintText isInvalid showError>
                The mailbox you connected uses a different address to your Ahoy
                sign-in, so Ahoy can&rsquo;t sync it yet. Connect the mailbox
                for the address you signed in with.
              </HintText>
            </div>
          )}

          <div className='flex flex-col gap-3'>
            <Button size='lg' className='w-full' onClick={goNext}>
              Continue
            </Button>
            <SkipButton onSkip={goNext} />
          </div>
        </div>
      );
      break;

    case 'ready':
      inner = (
        <div className='space-y-6'>
          <RadioGroup
            value={state.mailboxVisibility ?? undefined}
            onChange={handleVisibility}
            aria-label='What should Ahoy see?'
          >
            <RadioCard value='full' showIndicator>
              <p className='flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]'>
                {FULL_INBOX_TITLE}
                <Badge {...RECOMMENDED_BADGE_PROPS}>Recommended</Badge>
              </p>
              <p className='text-sm text-[var(--color-text-secondary)]'>
                {FULL_INBOX_BODY}
              </p>
            </RadioCard>
            <RadioCard value='exclude' showIndicator>
              <p className='text-sm font-semibold text-[var(--color-text-primary)]'>
                {EXCLUDE_TITLE}
              </p>
              <p className='text-sm text-[var(--color-text-secondary)]'>
                {EXCLUDE_BODY}
              </p>
            </RadioCard>
          </RadioGroup>

          {state.mailboxVisibility === 'exclude' && (
            <JoinExclusionEditor
              emails={state.filteredEmails}
              domains={state.filteredDomains}
              onEmailsChange={handleEmailsChange}
              onDomainsChange={handleDomainsChange}
            />
          )}

          {attempted && blocked && (
            <div role='alert'>
              <HintText isInvalid showError>
                Add at least one email or domain, or choose Full inbox.
              </HintText>
            </div>
          )}

          <Button size='lg' className='w-full' onClick={handleContinue}>
            Continue
          </Button>
        </div>
      );
      break;
  }

  return <div data-testid='join-step-permissions'>{inner}</div>;
}
